import { Box, getVariant } from '@apia/theme';
import { IAttachment } from '../types';
import { observer } from 'mobx-react-lite';
import { getLabel } from '@apia/util';
import { Collector } from '@apia/validations';
import { ApiaUtil, FileCard } from '@apia/components';
import type { TIconButton } from '@apia/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type TAttachmentPreviewType = 'audio' | 'image' | 'pdf';

const IMAGE_EXTENSIONS = new Set([
  'bmp',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'tif',
  'tiff',
  'webp',
]);
const AUDIO_EXTENSIONS = new Set([
  'aac',
  'flac',
  'm4a',
  'mp3',
  'oga',
  'ogg',
  'opus',
  'wav',
  'weba',
  'webm',
]);

function labelText(code: string, fallback: string) {
  const text = getLabel(code).text;
  if (!text || text.startsWith('LBL_NOT_FOUND')) {
    return fallback;
  }
  return text;
}

function getAttachmentExtension(name: string) {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function getDataUrlParts(value: string) {
  const dataUrlMatch = value.match(/^data:([^;,]+)?((?:;[^,]*)*),(.*)$/i);
  if (!dataUrlMatch) {
    return null;
  }

  return {
    data: dataUrlMatch[3],
    isBase64: /;base64/i.test(dataUrlMatch[2] ?? ''),
    mime: dataUrlMatch[1]?.toLowerCase() ?? '',
  };
}

function getAttachmentMime(attachment: IAttachment) {
  const dataUrlParts = getDataUrlParts(attachment.base64);
  if (dataUrlParts?.mime) {
    return dataUrlParts.mime;
  }

  const extension = getAttachmentExtension(attachment.name);
  if (IMAGE_EXTENSIONS.has(extension)) {
    return extension === 'svg' ? 'image/svg+xml' : `image/${extension}`;
  }
  if (extension === 'pdf') {
    return 'application/pdf';
  }
  if (AUDIO_EXTENSIONS.has(extension)) {
    return extension === 'mp3' ? 'audio/mpeg' : `audio/${extension}`;
  }

  return '';
}

function getAttachmentPreviewType(
  attachment: IAttachment,
): TAttachmentPreviewType | null {
  const mime = getAttachmentMime(attachment);
  if (mime.startsWith('image/')) {
    return 'image';
  }
  if (mime === 'application/pdf') {
    return 'pdf';
  }
  if (mime.startsWith('audio/')) {
    return 'audio';
  }

  return null;
}

function isReadyToUseSrc(value: string) {
  return /^(blob:|https?:\/\/|\/)/i.test(value);
}

function getAttachmentPreviewSrc(attachment: IAttachment) {
  if (
    attachment.base64.startsWith('data:') ||
    isReadyToUseSrc(attachment.base64)
  ) {
    return attachment.base64;
  }

  const mime = getAttachmentMime(attachment);
  const base64 = attachment.base64.replace(/\s/g, '');
  return mime ? `data:${mime};base64,${base64}` : attachment.base64;
}

function base64ToBlob(base64: string, mime: string) {
  const normalized = base64
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const binary = window.atob(normalized);
  const chunkSize = 1024;
  const chunks: Uint8Array[] = [];

  for (let index = 0; index < binary.length; index += chunkSize) {
    const chunk = binary.slice(index, index + chunkSize);
    const bytes = new Uint8Array(chunk.length);
    for (let byteIndex = 0; byteIndex < chunk.length; byteIndex++) {
      bytes[byteIndex] = chunk.charCodeAt(byteIndex);
    }
    chunks.push(bytes);
  }

  return new Blob(chunks, { type: mime });
}

function getAttachmentBlob(attachment: IAttachment, fallbackMime: string) {
  const dataUrlParts = getDataUrlParts(attachment.base64);
  const mime =
    dataUrlParts?.mime || getAttachmentMime(attachment) || fallbackMime;

  if (dataUrlParts) {
    if (dataUrlParts.isBase64) {
      return base64ToBlob(dataUrlParts.data, mime);
    }

    return new Blob([decodeURIComponent(dataUrlParts.data)], { type: mime });
  }

  return base64ToBlob(attachment.base64, mime);
}

const ImageAttachmentPreview = ({
  name,
  src,
}: {
  name: string;
  src: string;
}) => {
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
  } | null>(null);

  const resetView = useCallback(() => {
    setView({ scale: 1, x: 0, y: 0 });
  }, []);

  return (
    <Box
      className="attachmentPreview__imageViewport"
      onDoubleClick={resetView}
      onPointerDown={(ev) => {
        ev.currentTarget.setPointerCapture(ev.pointerId);
        dragRef.current = {
          pointerId: ev.pointerId,
          startX: ev.clientX,
          startY: ev.clientY,
          x: view.x,
          y: view.y,
        };
      }}
      onPointerMove={(ev) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== ev.pointerId) {
          return;
        }
        setView((current) => ({
          ...current,
          x: drag.x + ev.clientX - drag.startX,
          y: drag.y + ev.clientY - drag.startY,
        }));
      }}
      onPointerUp={(ev) => {
        if (dragRef.current?.pointerId === ev.pointerId) {
          dragRef.current = null;
        }
      }}
      onWheel={(ev) => {
        ev.preventDefault();
        const direction = ev.deltaY > 0 ? -1 : 1;
        setView((current) => ({
          ...current,
          scale: Math.min(6, Math.max(0.2, current.scale + direction * 0.15)),
        }));
      }}
      role="presentation"
    >
      <img
        alt={name}
        className="attachmentPreview__image"
        draggable={false}
        src={src}
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
        }}
      />
    </Box>
  );
};

const AttachmentPreview = ({
  attachment,
  previewType,
}: {
  attachment: IAttachment;
  previewType: TAttachmentPreviewType;
}) => {
  const src = getAttachmentPreviewSrc(attachment);
  const [pdfSrc, setPdfSrc] = useState('');

  useEffect(() => {
    if (previewType !== 'pdf') {
      setPdfSrc('');
      return undefined;
    }

    if (isReadyToUseSrc(attachment.base64)) {
      setPdfSrc(attachment.base64);
      return undefined;
    }

    let objectUrl = '';
    try {
      objectUrl = URL.createObjectURL(
        getAttachmentBlob(attachment, 'application/pdf'),
      );
      setPdfSrc(objectUrl);
    } catch {
      setPdfSrc(getAttachmentPreviewSrc(attachment));
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment, previewType]);

  return (
    <Box
      className={`attachmentPreview attachmentPreview--${previewType}`}
      {...getVariant('layout.common.chat.attachmentPreview')}
    >
      {previewType === 'image' && (
        <ImageAttachmentPreview name={attachment.name} src={src} />
      )}
      {previewType === 'pdf' && (
        <iframe
          className="attachmentPreview__pdf"
          src={pdfSrc}
          title={attachment.name}
        />
      )}
      {previewType === 'audio' && (
        <Box className="attachmentPreview__audio">
          <audio controls src={src}>
            {attachment.name}
          </audio>
        </Box>
      )}
    </Box>
  );
};

export const FileAttachment = observer(
  ({
    attachment,
    onAddDescription,
    onRemove,
  }: {
    attachment: IAttachment;
    onAddDescription?: (a: IAttachment, d: string) => unknown;
    onRemove?: () => unknown;
  }) => {
    const previewType = getAttachmentPreviewType(attachment);
    const previewLabel = labelText('prpPreview', 'Previsualizar');
    const additionalButtons = useMemo(() => {
      const buttons: TIconButton[] = [];

      if (previewType) {
        buttons.push({
          'aria-label': previewLabel,
          icon: 'View',
          onClick: (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            ApiaUtil.instance.modals.open({
              children: (
                <AttachmentPreview
                  attachment={attachment}
                  previewType={previewType}
                />
              ),
              size: previewType === 'audio' ? 'md-fixed' : 'xl-fixed',
              title: `${previewLabel}: ${attachment.name}`,
            });
          },
          title: previewLabel,
          variant: 'icon-only',
        });
      }

      if (onAddDescription) {
        buttons.push({
          icon: 'Pencil',
          onClick: () => {
            const collector = new Collector();
            collector.add(
              new collector.fields.textarea(
                'desc',
                getLabel('lblDes').text,
                { initialValue: attachment.description },
              ),
            );
            collector.show({
              onConfirm: (ev): boolean => {
                if (ev) {
                  onAddDescription(attachment, ev.desc! as string);
                }
                return true;
              },
              title: getLabel('btnAddComment').text,
            });
          },
          variant: 'icon-only',
        });
      }

      return buttons;
    }, [attachment, onAddDescription, previewLabel, previewType]);

    return (
      <FileCard
        // className="file_box__icon"
        name={attachment.name}
        handleClose={onRemove}
        additionalButtons={additionalButtons}
      />
    );
  },
);
